export enum UserRole {
  SUPERADMIN = 'user.role.superadmin',
  ADMIN = 'user.role.admin',
  MANAGER = 'user.role.manager',
  BRANCH_OFFICE = 'user.role.branch_office',
  SELLER = 'user.role.seller',
  WAREHOUSE_STAFF = 'user.role.warehouse_staff',
  CONSULTA = 'user.role.consulta',
  // Indicadores (2026-07-28): rol de SOLO lectura del tablero global. Entra al
  // admin pero solo ve "Indicadores" (no "Usuarios") — ver AdminLayout.navItems
  // y RoleService.getLandingRoute.
  INDICADORES = 'user.role.indicadores',
}

/**
 * Roles que pueden ENTRAR a esta aplicación (el panel admin). Es la compuerta
 * del login: un rol fuera de esta lista recibe "No tiene permisos para acceder
 * a esta aplicación" y se le limpia la sesión.
 *
 * OJO — es una lista aparte de lo que cada rol PUEDE HACER una vez dentro: eso
 * lo deciden `AdminLayout.navItems` (qué secciones ve) y los `@Roles(...)` del
 * backend (qué endpoints responde). Vive aquí, exportada, porque estaba
 * duplicada literal en los 2 caminos de `useAuth` (`initialize` y `login`) y
 * eso hizo que el rol INDICADORES quedara fuera al agregarlo: se podía crear la
 * cuenta, pero rebotaba en el login. Al agregar un rol nuevo al panel: agregarlo
 * AQUÍ además de en el menú y en el backend.
 *
 * MANAGER NO está: los gerentes consultan sus indicadores desde la app del
 * vendedor, no desde el panel (comportamiento previo, sin cambios).
 */
/**
 * Roles que ADMINISTRAN usuarios (ven la sección "Usuarios" y aterrizan ahí).
 * El resto de los roles con acceso al panel —hoy sólo `INDICADORES`— aterrizan
 * en el tablero.
 */
export const USER_ADMIN_ROLES: UserRole[] = [
  UserRole.SUPERADMIN,
  UserRole.ADMIN,
  UserRole.MANAGER,
];

/**
 * Ruta de aterrizaje tras iniciar sesión (y tras un cambio de contraseña
 * forzado, y al entrar a `/`). ÚNICA fuente de verdad: mandar a `/users` a un
 * rol que no administra usuarios lo deja frente a un 403 nada más entrar, que
 * es exactamente lo que le pasaba a `INDICADORES` — el login navegaba a
 * `/users` en duro, ignorando el landing por rol que ya existía.
 */
export function landingRouteForRole(role: UserRole | null | undefined): string {
  return role && USER_ADMIN_ROLES.includes(role) ? '/users' : '/indicadores';
}

export const ADMIN_APP_ROLES: UserRole[] = [
  UserRole.SUPERADMIN,
  UserRole.ADMIN,
  UserRole.INDICADORES,
];

export interface User {
  id: string;
  email: string;
  role: UserRole;
  branchOffice: number;
  userCode: string;
  externalUserId?: string | null;
  chainId?: string | null;
  regionId?: string | null;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null;
  isLoading: boolean;
  error: string | null;
}

export interface StorageData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface PasswordChangeError {
  code: 'PASSWORD_CHANGE_REQUIRED' | 'INVALID_PASSWORD' | 'UNAUTHORIZED';
  message: string;
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
  code?: string;
  /** Rutas de Consulta CM: el código del error (`CONFIRMAR_CUENTA_COMPARTIDA`, `VIGENCIA_ANTERIOR`…). */
  codigo?: string;
  /** El cuerpo completo del error, tal como lo mandó la API. */
  cuerpo?: Record<string, unknown>;
  requiresPasswordChange?: boolean;
} 